import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { TgWebApp } from '@/types/telegram';

interface TelegramContextValue {
  /** Объект Telegram.WebApp (или null, если приложение запущено вне Telegram). */
  webApp: TgWebApp | null;
  /** Готов ли SDK к работе. */
  isReady: boolean;
}

const TelegramContext = createContext<TelegramContextValue>({ webApp: null, isReady: false });

/**
 * Инициализирует Telegram WebApp SDK (грузится отдельным `<script>` в index.html)
 * и предоставляет его через контекст. Также включает безопасный режим
 * для разработки в обычном браузере (webApp = null).
 */
export function TelegramProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const wa = window.Telegram?.WebApp;
    if (wa) {
      wa.ready();
      wa.expand();
      try {
        wa.disableVerticalSwipes?.();
      } catch (err) {
        console.warn('[TelegramProvider] disableVerticalSwipes failed:', err);
      }
      try {
        wa.requestFullscreen?.();
      } catch (err) {
        console.warn('[TelegramProvider] requestFullscreen failed:', err);
      }
    }
    setIsReady(true);
  }, []);

  const value = useMemo<TelegramContextValue>(
    () => ({ webApp: window.Telegram?.WebApp ?? null, isReady }),
    [isReady],
  );

  return <TelegramContext.Provider value={value}>{children}</TelegramContext.Provider>;
}

/** Хук для доступа к Telegram WebApp SDK. */
export function useTelegram(): TelegramContextValue {
  return useContext(TelegramContext);
}
